import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Input, Skeleton, Button } from 'antd';
import { SearchOutlined, TeamOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { employeeService, Employee } from '../services/employeeService';
import { dashboardService, EmployeeAttendanceToday } from '../services/dashboardService';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PageHeader, EmptyState, PersonAvatar, Pill } from '../components/ui';
import dayjs from 'dayjs';
import './AttendancePage.css';

const AttendancePage: React.FC = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [today, setToday] = useState<EmployeeAttendanceToday[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [list, attendance] = await Promise.all([
        employeeService.getAll(),
        // Only to show who is on the clock right now; a failure here must not hide the list
        dashboardService.getEmployeesAttendanceToday().catch(() => [] as EmployeeAttendanceToday[]),
      ]);
      setEmployees(list);
      setToday(attendance);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error(t('attendance.fetchEmployeesError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The today endpoint returns names only, so that is what we match on
  const todayByName = useMemo(() => {
    const map = new Map<string, EmployeeAttendanceToday>();
    today.forEach((entry) => map.set(`${entry.name} ${entry.surname}`.toLowerCase(), entry));
    return map;
  }, [today]);

  const normalizedSearch = search.trim().toLowerCase();
  const visible = employees.filter(
    (employee) =>
      !normalizedSearch ||
      `${employee.name} ${employee.surname} ${employee.email}`.toLowerCase().includes(normalizedSearch),
  );

  const workingNow = today.filter((entry) => entry.first_check_in && !entry.last_check_out).length;

  return (
    <MainLayout bare>
      <PageHeader
        title={t('navigation.attendance')}
        subtitle={
          loading
            ? undefined
            : `${t('employees.showingCount', { count: employees.length })}${
                workingNow > 0 ? ` · ${t('attendance.workingNow', { count: workingNow })}` : ''
              }`
        }
        actions={
          <>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: 'var(--e22-text-muted)' }} />}
              placeholder={t('employees.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ width: 280 }}
            />
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchAll} disabled={loading}>
              {t('dashboard.refresh')}
            </Button>
          </>
        }
      />

      {loading ? (
        <Row gutter={[16, 16]}>
          {[0, 1, 2, 3].map((key) => (
            <Col key={key} xs={24} sm={12} lg={8} xxl={6}>
              <div className="e22-card e22-section">
                <Skeleton active avatar paragraph={{ rows: 1 }} />
              </div>
            </Col>
          ))}
        </Row>
      ) : visible.length === 0 ? (
        <div className="e22-card e22-section">
          <EmptyState
            icon={normalizedSearch ? <SearchOutlined /> : <TeamOutlined />}
            text={normalizedSearch ? t('employees.noMatching') : t('attendance.noEmployees')}
            action={
              normalizedSearch ? (
                <button type="button" className="e22-link-button" onClick={() => setSearch('')}>
                  {t('employees.clearSearch')}
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {visible.map((employee) => {
            const entry = todayByName.get(`${employee.name} ${employee.surname}`.toLowerCase());
            const working = !!entry?.first_check_in && !entry?.last_check_out;
            const done = !!entry?.first_check_in && !!entry?.last_check_out;
            return (
              <Col key={employee.id} xs={24} sm={12} lg={8} xxl={6}>
                <Link to={`/attendance-employee/${employee.id}`} className="e22-card att-person">
                  <PersonAvatar
                    name={employee.name}
                    surname={employee.surname}
                    imageUrl={employee.thumbnailUrl || employee.imageUrl}
                    size={48}
                  />
                  <div className="e22-row-body">
                    <div className="e22-row-title">
                      {employee.name} {employee.surname}
                    </div>
                    <div className="e22-row-subtitle">{employee.email}</div>
                    <div className="att-person-state">
                      {working ? (
                        <Pill tone="success" live>
                          {t('dashboard.workingSince', { time: dayjs(entry!.first_check_in!).format('HH:mm') })}
                        </Pill>
                      ) : done ? (
                        <Pill>{t('dashboard.filterDone')}</Pill>
                      ) : (
                        <Pill tone="neutral">{t('dashboard.notCheckedInToday')}</Pill>
                      )}
                    </div>
                  </div>
                  <RightOutlined className="att-person-arrow" />
                </Link>
              </Col>
            );
          })}
        </Row>
      )}
    </MainLayout>
  );
};

export default AttendancePage;
